import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellSlash } from "@phosphor-icons/react";

// Utility: URL-safe base64 → Uint8Array
function urlB64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function PushToggle({ warehouseId, size = "sm" }) {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permesso notifiche negato");
        return;
      }
      const { data } = await api.get("/push/vapid-public-key");
      if (!data.public_key) {
        toast.error("Notifiche push non configurate lato server");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.public_key),
      });
      const raw = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: raw.endpoint,
        keys: raw.keys,
        warehouse_id: warehouseId || null,
      });
      setSubscribed(true);
      toast.success("Notifiche attivate");
    } catch (e) {
      toast.error("Errore attivazione notifiche");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notifiche disattivate");
    } catch (e) {
      toast.error("Errore disattivazione");
    } finally {
      setBusy(false);
    }
  };

  if (!supported) return null;

  const isLg = size === "lg";
  const Icon = subscribed ? Bell : BellSlash;
  return (
    <button
      type="button"
      data-testid={`push-toggle-${warehouseId || "global"}`}
      onClick={subscribed ? disable : enable}
      disabled={busy}
      className={`flex items-center gap-1.5 border transition-colors font-medium ${isLg ? "px-4 py-2.5 text-sm" : "px-2 py-1 text-xs"} ${
        subscribed ? "border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"
      } disabled:opacity-50`}
      title={subscribed ? "Notifiche attive · clicca per disattivare" : "Attiva notifiche push"}
    >
      <Icon size={isLg ? 16 : 12} weight={subscribed ? "fill" : "bold"} />
      {isLg && (subscribed ? "Notifiche ON" : "Attiva notifiche")}
    </button>
  );
}
